export interface IRgb {
	r: number;
	g: number;
	b: number;
}

export function setOpacity(hex: string, opacity: number): string {
	const { r, g, b } = parseHexColor(hex);
	return getRgba(r, g, b, opacity);
}

export function getRgba(r: number, g: number, b: number, a: number): string {
	return `rgba(${r}, ${g}, ${b}, ${a})`;
}

export function hexToRgbStr(hexColor: string): string {
	const { r, g, b } = parseHexColor(hexColor);
	return `${r}, ${g}, ${b}`;
}

/**
 * based on color's brightness, returns either black or white transparent overlay color
 */
export function findOverlayColor(hex: string): string {
	const v = findOverlayValue(hex);
	return getRgba(v, v, v, 0.1);
}

/*
* Returns a raw r,g,b string of the overlay color, so alpha can be set in css:
* rgba(var(--overlay-color), 50%)
*/
export function findOverlayColorRgb(hex: string): string {
	const v = findOverlayValue(hex);
	return `${v}, ${v}, ${v}`;
}

function findOverlayValue(hex: string): number {
	const color = parseHexColor(hex);
	return getBrightness(color) > 0.5 ? 0 : 255;
}

export function blendOverlayColor(hex: string): IRgb {
	const color: IRgb = parseHexColor(hex);
	const blendValue = getBrightness(color) > 0.5 ? 0 : 255;
	const blendAlpha = 0.1;

	return {
		r: blend(blendValue, blendAlpha, color.r),
		g: blend(blendValue, blendAlpha, color.g),
		b: blend(blendValue, blendAlpha, color.b)
	};
}

function getBrightness({ r, g, b }: IRgb): number {
	return Math.max(r, g, b) / 255;
}

//Blend a single color channel value
function blend(srcVal: number, srcAlpha: number, destVal: number): number {
	return Math.round(srcVal * srcAlpha + destVal * (1 - srcAlpha));
}

function parseHexColor(hex: string): IRgb {
	const result = hex.replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
		.substring(1)
		.match(/.{2}/g)
		.map(x => parseInt(x, 16));

	return result && {
		r: result[0],
		g: result[1],
		b: result[2]
	};
}

export function lightenHexColor(hex: string, percent: number): string {
	const { r, g, b } = parseHexColor(hex);
	const newR = Math.min(255, Math.floor(r + (255 - r) * percent / 100));
	const newG = Math.min(255, Math.floor(g + (255 - g) * percent / 100));
	const newB = Math.min(255, Math.floor(b + (255 - b) * percent / 100));
	return rgbToHex(newR, newG, newB);
}

export function darkenHexColor(hex: string, percent: number): string {
	const { r, g, b } = parseHexColor(hex);
	const newR = Math.max(0, Math.floor(r * (1 - percent / 100)));
	const newG = Math.max(0, Math.floor(g * (1 - percent / 100)));
	const newB = Math.max(0, Math.floor(b * (1 - percent / 100)));
	return rgbToHex(newR, newG, newB);
}

function rgbToHex(r: number, g: number, b: number): string {
	return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

function componentToHex(c: number): string {
	const hex = c.toString(16);
	return hex.length == 1 ? '0' + hex : hex;
}
